'use client';

// STREAM-F3 — Globaler Theme-Switch (Sun/Moon) für Landing + Funnel.
//
// Optisch eng am AdminThemeToggle (animierter Thumb, Sun links, Moon rechts),
// aber Cookie-basiert via useGlobalTheme — d. h. der Wechsel ueberlebt
// page-transitions zwischen Landing und /bewerben. Wird oben rechts im
// Landing-Header (sticky bar) UND im FunnelShell-Header gerendert.
//
// Polish (A.7):
//  - Thumb-Bounce mit cubic-bezier(.2,.8,.3,1.5) (verspielt, ohne grosse Ueberschwinger
//    in horizontaler Richtung, weil x klein ist; das Bounce-Easing wirkt vor allem
//    auf die Skalierung während des Wechsels).
//  - 44x44 minimum Tap-Target (WCAG 2.5.5): unsichtbare Hit-Area um das visuelle
//    Switch-Track herum via padding + negative outline (visual size bleibt 62x32).
//  - aria-pressed zusaetzlich zu role=switch + aria-checked (manche Screenreader
//    behandeln pressed/checked unterschiedlich; doppelt haelt besser).
//  - Pfeil-Tastatur: ArrowLeft erzwingt Light, ArrowRight erzwingt Dark
//    (analog zu nativen <input type=range> mit zwei Stops).
//  - prefers-reduced-motion: animation duration auf 0 -> instant state-Wechsel.

import { Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useState, type KeyboardEvent } from 'react';
import { useGlobalTheme } from './useGlobalTheme';

interface GlobalThemeSwitchProps {
  /** Optional: kompaktere Variante (kleiner) - default für den Funnel-Header. */
  readonly size?: 'normal' | 'compact';
  /** Optional inline style override (z. B. position). */
  readonly style?: React.CSSProperties;
  /** Optional className-Hook. */
  readonly className?: string;
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else if (mq.addListener) mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else if (mq.removeListener) mq.removeListener(update);
    };
  }, []);
  return reduced;
}

export function GlobalThemeSwitch({ size = 'normal', style, className }: GlobalThemeSwitchProps) {
  const { theme, setTheme, toggle, mounted } = useGlobalTheme();
  const reducedMotion = usePrefersReducedMotion();
  const isDark = theme === 'dark';
  const label = isDark ? 'Zu Light Mode wechseln' : 'Zu Dark Mode wechseln';

  // Animationen erst NACH dem ersten Sync-Tick aktivieren — sonst spielt
  // die Bounce-Animation beim Page-Load ungewollt ab, wenn der Hook nach
  // Mount den Cookie-Wert (dark) uebernimmt und der Thumb von Light- in
  // Dark-Position "snappt". Wir warten einen Frame mit requestAnimationFrame
  // damit React zuerst den korrekten Initial-Zustand commitet hat.
  // Bei reduced-motion: harter Wechsel ohne Transition.
  const [postSync, setPostSync] = useState(false);
  useEffect(() => {
    if (!mounted) return;
    const id = requestAnimationFrame(() => setPostSync(true));
    return () => cancelAnimationFrame(id);
  }, [mounted]);
  const animate = postSync && !reducedMotion;
  // Smoothes Easing für Farben/Background (lineares Cubic-Bezier).
  const easeSmooth = animate ? '.35s cubic-bezier(.16,1,.3,1)' : '0s';
  // Bouncy Easing für den Thumb-Move (Polish-Ziel).
  const easeBounce = animate ? '.42s cubic-bezier(.2,.8,.3,1.5)' : '0s';

  const trackBg = isDark
    ? 'linear-gradient(135deg, #1C2336 0%, #0F1421 100%)'
    : 'linear-gradient(135deg, #F7F8FB 0%, #E2E6EF 100%)';

  // Kompakt-Variante für den Funnel-Header (eine Spur kleiner, weniger
  // visuelles Gewicht neben dem Brand-Wordmark).
  const dims = size === 'compact'
    ? { w: 54, h: 28, thumb: 22, iconLeft: 8, iconRight: 8, iconSize: 12, thumbOffLight: 2, thumbOffDark: 28 }
    : { w: 62, h: 32, thumb: 26, iconLeft: 9, iconRight: 9, iconSize: 14, thumbOffLight: 2, thumbOffDark: 32 };

  const thumbX = isDark ? dims.thumbOffDark : dims.thumbOffLight;

  // 44x44 Hit-Area: padding so dass total >= 44 in beiden Achsen.
  const padX = Math.max(0, Math.ceil((44 - dims.w) / 2));
  const padY = Math.max(0, Math.ceil((44 - dims.h) / 2));

  // Pfeil-Tasten -> explizite Wahl, Space/Enter -> toggle (native button macht das).
  const onKeyDown = useCallback(
    (evt: KeyboardEvent<HTMLButtonElement>) => {
      if (evt.key === 'ArrowLeft' || evt.key === 'Home') {
        evt.preventDefault();
        setTheme('light');
      } else if (evt.key === 'ArrowRight' || evt.key === 'End') {
        evt.preventDefault();
        setTheme('dark');
      }
    },
    [setTheme],
  );

  return (
    <button
      type="button"
      onClick={toggle}
      onKeyDown={onKeyDown}
      role="switch"
      aria-checked={isDark}
      aria-pressed={isDark}
      aria-label={label}
      title={label}
      className={className}
      style={{
        // Transparenter Hit-Area-Wrapper: padding sorgt für 44x44 minimum.
        // Hintergrund wird auf einem inneren span gemalt - so bleibt das
        // visuelle Switch klein, aber Touch/Stylus haben echten 44x44 Treffer.
        background: 'transparent',
        border: 'none',
        padding: `${padY}px ${padX}px`,
        margin: 0,
        position: 'relative',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        outline: 'none',
        flexShrink: 0,
        WebkitTapHighlightColor: 'transparent',
        ...style,
      }}
    >
      {/* Visueller Track */}
      <span
        aria-hidden="true"
        style={{
          background: trackBg,
          border: '1px solid var(--border-subtle)',
          borderRadius: 999,
          width: dims.w,
          height: dims.h,
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          boxShadow: 'inset 0 1px 2px rgba(10,14,26,0.10)',
          transition: `background ${easeSmooth}, border-color ${easeSmooth}`,
        }}
      >
        {/* Sonne (links) */}
        <Sun
          size={dims.iconSize}
          strokeWidth={2}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: dims.iconLeft,
            color: isDark ? '#4A5670' : '#F5A523',
            opacity: isDark ? 0.5 : 1,
            transform: isDark ? 'scale(0.85) rotate(-25deg)' : 'scale(1) rotate(0deg)',
            transformOrigin: 'center',
            transition: `color ${easeSmooth}, opacity ${easeSmooth}, transform ${easeBounce}`,
          }}
        />
        {/* Mond (rechts) */}
        <Moon
          size={dims.iconSize}
          strokeWidth={2}
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: dims.iconRight,
            color: isDark ? '#C8F542' : '#9AA5BC',
            opacity: isDark ? 1 : 0.5,
            transform: isDark ? 'scale(1) rotate(0deg)' : 'scale(0.85) rotate(25deg)',
            transformOrigin: 'center',
            transition: `color ${easeSmooth}, opacity ${easeSmooth}, transform ${easeBounce}`,
          }}
        />
        {/* Thumb */}
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 2,
            left: 0,
            width: dims.thumb,
            height: dims.thumb,
            borderRadius: '50%',
            background: isDark ? '#E5E9F0' : '#FFFFFF',
            boxShadow: '0 2px 6px rgba(10,14,26,0.22), 0 0 0 1px rgba(10,14,26,0.06)',
            transform: `translateX(${thumbX}px)`,
            transition: `transform ${easeBounce}, background ${easeSmooth}`,
          }}
        />
      </span>
    </button>
  );
}
